import type {
  AcceptClinicianInviteResponse,
  AcceptInviteRequest,
  AcceptPatientInviteResponse,
  RegisterAccountRequest,
  RegisterAccountResponse,
  VerifyAccountResponse,
} from '@fatigue/shared';
import { API_BASE_URL } from '../config';

/**
 * The unauthenticated routes this site calls: registering an organisation,
 * confirming its owner's address, and a clinician or a patient accepting an
 * invitation.
 *
 * Separate from `data/api.ts` because everything there carries a token and
 * answers a 401 by signing out. These have no session to send or lose.
 */

export class PublicError extends Error {}

async function post<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new PublicError('Could not reach the server. Check your connection and try again.');
  }

  if (response.status === 429) {
    // These routes are throttled to about one request a second, account-wide.
    throw new PublicError('Too many attempts just now. Wait a minute and try again.');
  }

  if (!response.ok) {
    let message = 'Something went wrong. Try again in a moment.';
    try {
      const failure = (await response.json()) as { message?: unknown };
      // The API words its refusals to be shown — "That invitation has already
      // been used" — so they are passed through rather than paraphrased.
      if (typeof failure.message === 'string' && failure.message !== '') message = failure.message;
    } catch {
      // Keep the generic message.
    }
    throw new PublicError(message);
  }

  return (await response.json()) as T;
}

export function registerAccount(request: RegisterAccountRequest): Promise<RegisterAccountResponse> {
  return post('/accounts', request);
}

export function verifyAccount(token: string): Promise<VerifyAccountResponse> {
  return post('/accounts/verify', { token });
}

export function acceptClinicianInvite(
  request: AcceptInviteRequest,
): Promise<AcceptClinicianInviteResponse> {
  return post('/invites/clinician/accept', request);
}

/**
 * A patient choosing their password. Done here, in the browser the emailed
 * link opens, rather than in the phone app: the app then needs only a sign-in
 * screen, and the token never has to be handed from an email to an app.
 */
export function acceptPatientInvite(
  request: AcceptInviteRequest,
): Promise<AcceptPatientInviteResponse> {
  return post('/invites/patient/accept', request);
}

/**
 * A patient choosing a new password from a reset link their clinician sent.
 * Sets it on their existing account and signs their app out everywhere.
 */
export function resetPatientPassword(request: AcceptInviteRequest): Promise<{ reset: true }> {
  return post('/invites/patient/reset', request);
}

/**
 * The emailed token, read once and then removed from the address bar.
 *
 * It is a bearer credential until it is redeemed, so it should not sit in the
 * browser history or be on screen for anyone glancing over a shoulder. The
 * page holds it in memory from here on.
 */
export function takeTokenFromUrl(): string | null {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');
  if (token !== null) {
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
  return token === null || token === '' ? null : token;
}

/** Matches the API's `MIN_PASSWORD_LENGTH` and both pools' password policy. */
export const MIN_PASSWORD_LENGTH = 12;
